import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
// Using Ant Design Select directly since our Select wrapper has different API
import { Spin, Empty, Select } from 'antd';
import {
  FilePdf,
  FileImage,
  File,
  CaretDown,
  User,
} from '@phosphor-icons/react';
import Typography from '../../components/Typography';
import Button from '../../components/Button';
import { formatFileSize } from '../../components/portal/formatUtils';
import { formatDate } from '../../components/portal/dateUtils';
import './documentsPage.scss';

const PER_PAGE_OPTIONS = [
  { value: 10, label: '10 per page' },
  { value: 20, label: '20 per page' },
  { value: 50, label: '50 per page' },
];

const getFileIcon = (contentType, extension) => {
  if (contentType === 'application/pdf' || extension === '.pdf') {
    return (
      <div className="documents-page__file-icon documents-page__file-icon--pdf">
        <FilePdf size={18} weight="fill" />
      </div>
    );
  }
  if (contentType && contentType.startsWith('image/')) {
    return (
      <div className="documents-page__file-icon documents-page__file-icon--image">
        <FileImage size={18} weight="fill" />
      </div>
    );
  }
  return (
    <div className="documents-page__file-icon documents-page__file-icon--default">
      <File size={18} weight="fill" />
    </div>
  );
};

const DocumentsPage = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    perPage: 20,
  });

  const fetchDocuments = useCallback(async (page, perPage) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/api/v1/referral_attachments', {
        params: { page, per_page: perPage },
      });
      const { data } = response;
      setGroups(data.groups || []);
      setPagination({
        currentPage: data.pagination.current_page,
        totalPages: data.pagination.total_pages,
        totalCount: data.pagination.total_count,
        perPage,
      });

      // Auto-expand all groups on load
      const expanded = {};
      (data.groups || []).forEach((group) => {
        expanded[group.referral_entity.slug] = true;
      });
      setExpandedGroups(expanded);
    } catch (err) {
      console.error('Failed to load documents:', err);
      setError('Failed to load documents. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments(pagination.currentPage, pagination.perPage);
  }, [pagination.currentPage, pagination.perPage]);

  const handleToggleGroup = (slug) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [slug]: !prev[slug],
    }));
  };

  const handleDocumentClick = async (entitySlug, attachmentSlug) => {
    try {
      const response = await axios.get(
        `/api/v1/referral_entities/${entitySlug}/attachments/${attachmentSlug}`,
      );
      const { download_url: downloadUrl } = response.data;
      if (downloadUrl) {
        window.open(downloadUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('Failed to download document:', err);
    }
  };

  const handlePerPageChange = (value) => {
    setPagination((prev) => ({
      ...prev,
      perPage: value,
      currentPage: 1,
    }));
  };

  const handlePreviousPage = () => {
    setPagination((prev) => ({
      ...prev,
      currentPage: prev.currentPage - 1,
    }));
  };

  const handleNextPage = () => {
    setPagination((prev) => ({
      ...prev,
      currentPage: prev.currentPage + 1,
    }));
  };

  const startItem = (pagination.currentPage - 1) * pagination.perPage + 1;
  const endItem = Math.min(
    pagination.currentPage * pagination.perPage,
    pagination.totalCount,
  );

  if (loading) {
    return (
      <div className="documents-page">
        <div className="documents-page__header">
          <Typography variant="h1" size="large" bold>
            Documents
          </Typography>
          <Typography variant="text" size="small" className="documents-page__subtitle">
            View all documents shared across your referrals
          </Typography>
        </div>
        <div className="documents-page__loading">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="documents-page">
        <div className="documents-page__header">
          <Typography variant="h1" size="large" bold>
            Documents
          </Typography>
          <Typography variant="text" size="small" className="documents-page__subtitle">
            View all documents shared across your referrals
          </Typography>
        </div>
        <div className="documents-page__error">
          <div className="documents-page__error-content">
            <Typography variant="text" size="small">
              {error}
            </Typography>
            <Button
              type="secondary"
              size="small"
              label="Retry"
              className="documents-page__retry-btn"
              onClick={() => fetchDocuments(pagination.currentPage, pagination.perPage)}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="documents-page">
      <div className="documents-page__header">
        <Typography variant="h1" size="large" bold>
          Documents
        </Typography>
        <Typography variant="text" size="small" className="documents-page__subtitle">
          View all documents shared across your referrals
        </Typography>
      </div>

      {groups.length === 0 ? (
        <div className="documents-page__empty">
          <Empty description="No documents available" />
        </div>
      ) : (
        <>
          {groups.map((group) => {
            const { referral_entity: referralEntity, attachments } = group;
            const isExpanded = expandedGroups[referralEntity.slug];
            const activeAttachments = attachments.filter((a) => !a.deleted);

            return (
              <div key={referralEntity.slug} className="documents-page__group">
                <div
                  className="documents-page__group-header"
                  onClick={() => handleToggleGroup(referralEntity.slug)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleToggleGroup(referralEntity.slug);
                    }
                  }}
                >
                  <div className="documents-page__group-title">
                    <User size={16} weight="bold" />
                    <span className="documents-page__group-patient-name">
                      {referralEntity.patient_name || 'Unknown Patient'}
                    </span>
                    <span className="documents-page__group-count">
                      {activeAttachments.length}
                    </span>
                  </div>
                  <div
                    className={`documents-page__group-toggle ${
                      isExpanded ? 'documents-page__group-toggle--expanded' : ''
                    }`}
                  >
                    <CaretDown size={16} weight="bold" />
                  </div>
                </div>

                <div
                  className={`documents-page__group-body ${
                    isExpanded ? 'documents-page__group-body--expanded' : ''
                  }`}
                >
                  <table className="documents-page__document-table">
                    <thead>
                      <tr>
                        <th style={{ width: '40%' }}>FILE</th>
                        <th style={{ width: '25%' }}>UPLOADED BY</th>
                        <th style={{ width: '15%' }}>SIZE</th>
                        <th style={{ width: '20%' }}>DATE</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeAttachments.map((attachment) => (
                        <tr
                          key={attachment.slug}
                          onClick={() => handleDocumentClick(referralEntity.slug, attachment.slug)}
                        >
                          <td>
                            <div className="documents-page__file-info">
                              {getFileIcon(attachment.content_type, attachment.file_extension)}
                              <div>
                                <div className="documents-page__file-name">
                                  {attachment.original_filename}
                                </div>
                                <div className="documents-page__file-extension">
                                  {attachment.file_extension
                                    ? attachment.file_extension.replace('.', '')
                                    : ''}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="documents-page__uploader-name">
                              {attachment.uploaded_by?.user_name || 'Unknown'}
                            </div>
                            <div className="documents-page__uploader-org">
                              {attachment.uploaded_by?.organization_name || ''}
                            </div>
                          </td>
                          <td>
                            <span className="documents-page__file-size">
                              {formatFileSize(attachment.file_size)}
                            </span>
                          </td>
                          <td>
                            <span className="documents-page__date">
                              {formatDate(attachment.created_at)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}

          <div className="documents-page__pagination">
            <Typography variant="text" size="small" className="documents-page__pagination-info">
              Showing
              {' '}
              {startItem}
              {' '}
              to
              {' '}
              {endItem}
              {' '}
              of
              {' '}
              {pagination.totalCount}
            </Typography>

            <div className="documents-page__pagination-controls">
              <Select
                value={pagination.perPage}
                onChange={handlePerPageChange}
                options={PER_PAGE_OPTIONS}
                className="documents-page__per-page"
              />

              <Typography variant="text" size="small" className="documents-page__page-info">
                Page
                {' '}
                {pagination.currentPage}
                {' '}
                of
                {' '}
                {pagination.totalPages || 1}
              </Typography>

              <div className="documents-page__pagination-nav">
                <Button
                  type="secondary"
                  size="small"
                  label="Previous"
                  disabled={pagination.currentPage === 1}
                  onClick={handlePreviousPage}
                />
                <Button
                  type="secondary"
                  size="small"
                  label="Next"
                  disabled={pagination.currentPage >= pagination.totalPages}
                  onClick={handleNextPage}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DocumentsPage;
